/**
 * Ice Storm Fusion Anomaly detail panel (operator-cop-doctrine §4.5 / §3B.8, IMS
 * §7.2). A SCORED anomaly (carries an IMS Response Level) raised when an outage
 * cluster corroborates with a CAP freezing-rain/ice-storm warning (anticipatory) or
 * is large on its own merit (impact). The act-register signal on top of the ambient
 * Power Outages layer. Two operator actions:
 *   • Acknowledge → demotes in the queue; marker stays (§6.1).
 *   • Dismiss     → logged false positive; auto-leaves the queue.
 * It also auto-clears when the cluster dissipates or the warning expires.
 */
import { useEffect, useState, type ComponentProps } from 'react';
import {
  acknowledgeIceStormAnomaly,
  dismissIceStormAnomaly,
  fetchIceStormAnomalyDetail,
  fetchIncidentForSource,
  type IceStormAnomalyFeature,
  type IceCommunityInReach,
} from './api';
import { iceTypeLabel } from './iceStormAnomalyStyle';
import { AnomalyResponseLevelBadge } from './AnomalyResponseLevelBadge';
import { OwnershipCoordinationBlock } from './components/OwnershipCoordinationBlock';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { IncidentWorkSurface } from './SidePanel';

/** The IncidentWorkSurface callback contract, reused so the ice-storm panel threads the
 *  same handlers the fire body does (no re-typing the feature shapes). */
type IncidentSurfaceProps = ComponentProps<typeof IncidentWorkSurface>;

function agoLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

function expiryLabel(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso) - Date.now();
  if (!Number.isFinite(ms)) return '—';
  if (ms <= 0) return 'expired';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `in ${min}m`;
  return `in ${Math.round(min / 60)}h`;
}

export function IceStormAnomalyBody({
  anomaly,
  onResolved,
  onOpenEvacuation,
  onSelectCamera,
  onSelectAsset,
  onSelectAircraft,
  onOpenSitrep,
  onOpenConnectedAssist,
  onOpenReplay,
  onSelectMission,
  aarReplayActive = false,
}: {
  anomaly: IceStormAnomalyFeature;
  /** Called after acknowledge/dismiss so the parent refreshes the queue + selection. */
  onResolved?: (outcome: 'acknowledged' | 'dismissed') => void;
  /** Open the incident-scoped Evacuation Logistics surface (§4.4). */
  onOpenEvacuation?: (incidentId: string) => void;
  /** #425 — IncidentWorkSurface handlers, threaded from the SidePanel body switch so a
   *  declared ice-storm incident is worked from the same surface a fire is. */
  onSelectCamera?: IncidentSurfaceProps['onSelectCamera'];
  onSelectAsset?: IncidentSurfaceProps['onSelectAsset'];
  onSelectAircraft?: IncidentSurfaceProps['onSelectAircraft'];
  onOpenSitrep?: IncidentSurfaceProps['onOpenSitrep'];
  onOpenConnectedAssist?: IncidentSurfaceProps['onOpenConnectedAssist'];
  onOpenReplay?: IncidentSurfaceProps['onOpenReplay'];
  onSelectMission?: IncidentSurfaceProps['onSelectMission'];
  aarReplayActive?: boolean;
}): JSX.Element {
  const p = anomaly.properties;
  const [lon, lat] = anomaly.geometry.coordinates;
  const [busy, setBusy] = useState<'ack' | 'dismiss' | null>(null);
  const [reason, setReason] = useState('');
  const [communities, setCommunities] = useState<IceCommunityInReach[]>([]);
  // #425 — the incident promoted FROM this ice-storm anomaly (source_record link), if any.
  const [incidentId, setIncidentId] = useState<string | null>(null);

  useEffect(() => {
    logAction('view_ice_storm_anomaly_detail', { anomaly_id: anomaly.id });
    const ctl = new AbortController();
    void (async () => {
      try {
        const detail = await fetchIceStormAnomalyDetail(anomaly.id, ctl.signal);
        setCommunities(detail.properties.communities_in_reach ?? []);
      } catch {
        /* detail enrichment is best-effort */
      }
    })();
    return () => ctl.abort();
  }, [anomaly.id]);

  // #425 — resolve the incident promoted from THIS anomaly (idempotent on the source).
  useEffect(() => {
    setIncidentId(null);
    const ctl = new AbortController();
    void fetchIncidentForSource('ice_storm_anomaly', anomaly.id, ctl.signal)
      .then((inc) => setIncidentId(inc?.id ?? null))
      .catch(() => setIncidentId(null));
    return () => ctl.abort();
  }, [anomaly.id]);

  const acknowledge = async (): Promise<void> => {
    setBusy('ack');
    try {
      const sessionId = getSessionId();
      logAction('acknowledge_ice_storm_anomaly', { anomaly_id: anomaly.id });
      await acknowledgeIceStormAnomaly(anomaly.id, sessionId);
      showToast('Ice-storm anomaly acknowledged — demoted in the queue; marker stays.', {
        variant: 'success',
      });
      onResolved?.('acknowledged');
    } catch {
      showToast('Could not acknowledge the anomaly.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (): Promise<void> => {
    setBusy('dismiss');
    try {
      const sessionId = getSessionId();
      logAction('dismiss_ice_storm_anomaly', { anomaly_id: anomaly.id, reason: reason || null });
      await dismissIceStormAnomaly(anomaly.id, sessionId, reason || undefined);
      showToast('Ice-storm anomaly dismissed — logged as a false positive. Left the queue.', {
        variant: 'success',
      });
      onResolved?.('dismissed');
    } catch {
      showToast('Could not dismiss the anomaly.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const customers = p.customers_affected;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="ice-storm-anomaly-body">
      <div className="border-l-2 border-sky-400 pl-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          ❄ Ice-storm anomaly · {iceTypeLabel(p.anomaly_type)}
        </p>
        <h2 className="text-base font-semibold">Power-outage cluster</h2>
        <div className="mt-1.5">
          {/* #445-ws2 — the operative (IC-set) Response Level over the annotated system-derived. */}
          <AnomalyResponseLevelBadge
            derived={p.response_level}
            operatorSet={p.operator_response_level ?? null}
            assessedBy={p.operator_rl_by ?? null}
            reason={p.operator_rl_reason ?? null}
            testidPrefix="ice-storm-anomaly-response-level"
            extra={
              p.status === 'acknowledged' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                  acknowledged
                </span>
              )
            }
          />
        </div>
      </div>

      {/* Ownership-coordination — the cross-agency "who holds command + how to reach them" read. */}
      {p.owned_incident && <OwnershipCoordinationBlock owned={p.owned_incident} />}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Outage cluster</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Outages</dt>
          <dd className="font-mono">{p.outage_count}</dd>
          <dt className="text-slate-400">Customers affected</dt>
          <dd className="font-mono">{customers != null ? customers.toLocaleString() : '—'}</dd>
          <dt className="text-slate-400">Utilities</dt>
          <dd>{p.utilities.length ? p.utilities.join(', ') : '—'}</dd>
          <dt className="text-slate-400">Cluster extent</dt>
          <dd className="font-mono">{(p.radius_m / 1000).toFixed(1)} km</dd>
          <dt className="text-slate-400">First detected</dt>
          <dd>{agoLabel(p.first_detected_at)}</dd>
          <dt className="text-slate-400">Centre</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      <section className="border-t border-slate-800 pt-3">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Corroborating warning
        </h3>
        {p.cap_event ? (
          <dl className="grid grid-cols-2 gap-y-1 text-sm">
            <dt className="text-slate-400">Warning</dt>
            <dd>{p.cap_event}</dd>
            <dt className="text-slate-400">Severity</dt>
            <dd>{p.cap_severity ?? '—'}</dd>
            <dt className="text-slate-400">Expires</dt>
            <dd>{expiryLabel(p.cap_expires_at)}</dd>
          </dl>
        ) : (
          <p className="text-[11px] text-slate-400 leading-relaxed">
            No active freezing-rain / ice-storm warning overlaps this cluster — it qualifies
            as an <span className="font-medium">impact</span> anomaly on its own scale (well
            above the routine-outage baseline).
          </p>
        )}
      </section>

      <section className="border-t border-slate-800 pt-3">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Communities in reach{communities.length ? ` · ${communities.length}` : ''}
        </h3>
        {communities.length ? (
          <ul className="text-sm space-y-0.5 max-h-40 overflow-y-auto">
            {communities.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2">
                <span className="truncate">{c.name}</span>
                <span className="font-mono text-xs text-slate-400 shrink-0">
                  {c.vulnerable_population != null ? `${c.vulnerable_population} vuln · ` : ''}
                  {c.distance_km} km
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] text-slate-500">No tracked communities within reach.</p>
        )}
      </section>

      {/* #425 — declare-from-any-hazard: the SAME IncidentWorkSurface the fire body
          mounts, anchored on this ice-storm anomaly. Unowned ⇒ one-click Declare; once
          owned it opens to the incident management section (op-periods, resources,
          drones, SITREP, evac). */}
      <section className="border-t border-slate-800 pt-3" data-testid="ice-storm-incident-surface">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Incident</h3>
        <IncidentWorkSurface
          source={{
            recordType: 'ice_storm_anomaly',
            recordId: anomaly.id,
            lat,
            lon,
            aoLabel: 'this ice-storm anomaly',
            declareName: `Ice storm — ${p.utilities.length ? p.utilities.join('/') : 'outage cluster'} (${lat.toFixed(3)}, ${lon.toFixed(3)})`,
            declareHazardType: 'ice_storm',
          }}
          incidentId={incidentId}
          lat={lat}
          lon={lon}
          cameraTuning="all-nearby"
          onIncidentDeclared={(inc) => setIncidentId(inc.id)}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenSitrep={onOpenSitrep}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onOpenEvacuation={onOpenEvacuation}
          onOpenReplay={onOpenReplay}
          onSelectMission={onSelectMission}
          replayActive={aarReplayActive}
        />
      </section>

      <section className="border-t border-slate-800 pt-3 space-y-2">
        <label className="block text-[11px] text-slate-400" htmlFor="ice-dismiss-reason">
          Dismiss reason (optional)
        </label>
        <input
          id="ice-dismiss-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. planned maintenance / not ice-related"
          className="w-full rounded bg-slate-800 border border-slate-700 px-2 py-1 text-sm"
        />
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            disabled={busy !== null || p.status === 'acknowledged'}
            onClick={() => void acknowledge()}
            className="flex-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 px-3 py-2 text-sm font-medium"
          >
            {busy === 'ack' ? 'Acknowledging…' : 'Acknowledge'}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void dismiss()}
            className="flex-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-2 text-sm font-medium"
          >
            {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Acknowledge demotes it in the queue (marker stays); dismiss logs a false positive.
          The anomaly also auto-clears when the cluster clears or the warning expires. Both
          actions are audited (no hard delete).
        </p>
      </section>

    </div>
  );
}
